import type { DbRequest, RpcMessage, RpcReply, SqlParam } from './types'

let worker: Worker | null = null
let nextId = 1
const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>()

function ensureWorker(): Worker {
  if (worker) return worker
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
  worker.addEventListener('message', (event: MessageEvent<RpcReply>) => {
    const { id, ok, result, error } = event.data
    const slot = pending.get(id)
    if (!slot) return
    pending.delete(id)
    if (ok) slot.resolve(result)
    else slot.reject(new Error(error ?? 'Unknown DB error'))
  })
  worker.addEventListener('error', (event) => {
    const err = new Error(`DB worker error: ${event.message}`)
    for (const slot of pending.values()) slot.reject(err)
    pending.clear()
    worker?.terminate()
    worker = null
  })
  return worker
}

function send<T>(request: DbRequest): Promise<T> {
  const w = ensureWorker()
  const id = nextId++
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject })
    const msg: RpcMessage = { id, request }
    w.postMessage(msg)
  })
}

export function exec(sql: string, params?: SqlParam[]): Promise<void> {
  return send({ kind: 'exec', sql, params })
}

export function query<T = Record<string, unknown>>(sql: string, params?: SqlParam[]): Promise<T[]> {
  return send<T[]>({ kind: 'query', sql, params })
}

export function batch(statements: { sql: string; params?: SqlParam[] }[]): Promise<void> {
  return send({ kind: 'batch', statements })
}

export function resetClient(): void {
  worker?.terminate()
  worker = null
  for (const slot of pending.values()) slot.reject(new Error('DB client reset'))
  pending.clear()
}
